"use client"
import { useState, useMemo, useEffect, useRef } from "react"
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Input,
  Select,
  Switch,
  useDisclosure,
  useToast,
  FormControl,
  FormLabel,
  NumberInput,
  NumberInputField,
  VStack,
  HStack,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Flex,
  IconButton,
  Container,
  TableContainer,
  Checkbox,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Spinner,
} from "@chakra-ui/react"
import { AddIcon, ViewIcon, EditIcon, DeleteIcon, ChevronDownIcon } from "@chakra-ui/icons"
import { useDispatch, useSelector } from "react-redux"
import {
  addSubscriptionCustomAsync,
  deleteSubscriptionCustomAsync,
  fetchSubscriptionCustomAsync,
  selectSubscriptionCustom,
  updateSubscriptionCustomAsync,
} from "../../Subscriptions/SubscriptionCustom/SubscriptionCustomSlice"
import { fetchEmployersForDropdownAsync, selectAllEmployers } from "../../EmployerData/EmployerDataSlice"
import { fetchSubscriptionasync, selectSubscription } from "../../Subscriptions/SubscriptionDetails/SubscriptionSlice"

const initialPlanState = {
  user_uuid: "",
  subscription_uuid: "", // This will now be dynamic
  amount: "",
  validity: "",
  validity_type: "day",
  status: "active",
  profile_creation: false,
  notification_via_email_sms_whatsapp: false,
  no_of_job_post: 0,
  no_of_urgent_hiring_tag: 0,
  no_of_candidate_unlock: 0,
  no_of_job_published: 0,
  job_boosts: false,
  smart_boost_via_email_sms_whatsapp: false,
  dedicated_relationship_manager: false,
  campion_via_email_sms_whatsapp: false,
  multiple_user_login: false,
  reports: false,
  company_branding: false,
  job_applicants_download: false, // New field
  unlocked_candidate_download: false, // New field
}

const SubscriptionCustom = () => {
  const dispatch = useDispatch()
  const subscriptions = useSelector(selectSubscriptionCustom)
  const allSubscriptions = useSelector(selectSubscription) // Renamed to avoid confusion with custom subscriptions
  const allEmployers = useSelector(selectAllEmployers)
  const employerDropdownPagination = useSelector((state) => state.employerData.employerDropdownPagination)
  const isFetchingMoreEmployers = useSelector((state) => state.employerData.isFetchingMoreEmployers)

  const [currentPlan, setCurrentPlan] = useState(initialPlanState)
  const [selectedPlans, setSelectedPlans] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedEmployerName, setSelectedEmployerName] = useState("") // For displaying selected employer in dropdown
  const [selectedSubscriptionTitle, setSelectedSubscriptionTitle] = useState("") // For displaying selected subscription in dropdown

  const { isOpen: isAddOpen, onOpen: onAddOpen, onClose: onAddClose } = useDisclosure()
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure()
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure()
  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure()
  const toast = useToast()
  const menuListRef = useRef(null) // Ref for the employer dropdown menu list

  useEffect(() => {
    dispatch(fetchSubscriptionCustomAsync())
    dispatch(fetchSubscriptionasync()) // Fetch all subscriptions
    dispatch(fetchEmployersForDropdownAsync(1))
  }, [dispatch])

  // Filter employer subscriptions for the dropdown
  const employerSubscriptionOptions = useMemo(() => {
    if (!Array.isArray(allSubscriptions)) return []
    return allSubscriptions.filter((sub) => sub.type === "employer")
  }, [allSubscriptions])

  const handleEditPlan = (plan) => {
    setCurrentPlan({
      ...plan,
      amount: plan.amount || 0,
      validity: plan.validity || 1,
      no_of_job_post: plan.no_of_job_post || 0,
      no_of_urgent_hiring_tag: plan.no_of_urgent_hiring_tag || 0,
      no_of_candidate_unlock: plan.no_of_candidate_unlock || 0,
      no_of_job_published: plan.no_of_job_published || 0,
      job_applicants_download: plan.job_applicants_download || false, // Initialize new field
      unlocked_candidate_download: plan.unlocked_candidate_download || false, // Initialize new field
    })
    setSelectedEmployerName(plan?.employer?.name || plan?.employer?.email || "Select a user")

    // Set the selected subscription title for display in the edit modal
    const selectedSub = employerSubscriptionOptions.find((sub) => sub.uuid === plan.subscription_uuid)
    setSelectedSubscriptionTitle(selectedSub ? `${selectedSub.title} - ₹${selectedSub.amount}` : "")

    onEditOpen()
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setCurrentPlan((prevPlan) => ({
      ...prevPlan,
      [name]: value || "",
    }))
  }

  const handleSwitchChange = (name) => {
    setCurrentPlan((prevPlan) => ({
      ...prevPlan,
      [name]: !prevPlan[name],
    }))
  }

  const handleNumberInputChange = (name, value) => {
    setCurrentPlan((prevPlan) => ({
      ...prevPlan,
      [name]: Number(value) || 0,
    }))
  }

  const handleAddPlan = async () => {
    try {
      const newPlan = {
        ...currentPlan,
        amount: Number.parseFloat(currentPlan.amount),
        validity: Number.parseInt(currentPlan.validity),
        no_of_job_post: Number.parseInt(currentPlan.no_of_job_post),
        no_of_urgent_hiring_tag: Number.parseInt(currentPlan.no_of_urgent_hiring_tag),
        no_of_candidate_unlock: Number.parseInt(currentPlan.no_of_candidate_unlock),
        no_of_job_published: Number.parseInt(currentPlan.no_of_job_published),
      }
      await dispatch(addSubscriptionCustomAsync(newPlan)).unwrap()
      toast({
        title: "Plan added successfully.",
        status: "success",
        duration: 2000,
        isClosable: true,
      })
      setCurrentPlan(initialPlanState)
      setSelectedEmployerName("")
      setSelectedSubscriptionTitle("") // Reset selected subscription title
      onAddClose()
      dispatch(fetchSubscriptionCustomAsync())
    } catch (error) {
      toast({
        title: "Failed to add plan.",
        description: error.toString(),
        status: "error",
        duration: 2000,
        isClosable: true,
      })
    }
  }

  const handleUpdatePlan = async () => {
    try {
      const updatedPlan = {
        ...currentPlan,
        amount: Number.parseFloat(currentPlan.amount),
        validity: Number.parseInt(currentPlan.validity),
        no_of_job_post: Number.parseInt(currentPlan.no_of_job_post),
        no_of_urgent_hiring_tag: Number.parseInt(currentPlan.no_of_urgent_hiring_tag),
        no_of_candidate_unlock: Number.parseInt(currentPlan.no_of_candidate_unlock),
        no_of_job_published: Number.parseInt(currentPlan.no_of_job_published),
      }
      await dispatch(
        updateSubscriptionCustomAsync({
          id: currentPlan.uuid,
          updateSubscriptiondata: updatedPlan,
        }),
      ).unwrap()
      toast({
        title: "Plan updated successfully.",
        status: "success",
        duration: 2000,
        isClosable: true,
      })
      setCurrentPlan(initialPlanState)
      setSelectedEmployerName("")
      setSelectedSubscriptionTitle("") // Reset selected subscription title
      onEditClose()
      dispatch(fetchSubscriptionCustomAsync())
    } catch (error) {
      toast({
        title: "Failed to update plan.",
        description: error.toString(),
        status: "error",
        duration: 2000,
        isClosable: true,
      })
    }
  }

  const handleDeletePlan = async () => {
    try {
      await dispatch(deleteSubscriptionCustomAsync(currentPlan.uuid)).unwrap()
      toast({
        title: "Plan deleted successfully.",
        status: "success",
        duration: 2000,
        isClosable: true,
      })
      setCurrentPlan(initialPlanState)
      setSelectedEmployerName("")
      setSelectedSubscriptionTitle("") // Reset selected subscription title
      onDeleteClose()
      dispatch(fetchSubscriptionCustomAsync())
    } catch (error) {
      toast({
        title: "Failed to delete plan.",
        description: error.toString(),
        status: "error",
        duration: 2000,
        isClosable: true,
      })
    }
  }

  const handleDeleteConfirmation = (planUuid) => {
    const planToDelete = subscriptions.find((plan) => plan.uuid === planUuid)
    if (planToDelete) {
      setCurrentPlan(planToDelete)
      onDeleteOpen()
    }
  }

  const handleViewPlan = (plan) => {
    setCurrentPlan(plan)
    onViewOpen()
  }

  const togglePlanSelection = (planUuid) => {
    if (selectedPlans.includes(planUuid)) {
      setSelectedPlans(selectedPlans.filter((id) => id !== planUuid))
    } else {
      setSelectedPlans([...selectedPlans, planUuid])
    }
  }

  // Filter plans based on search term
  const filteredPlans = useMemo(() => {
    if (!Array.isArray(subscriptions)) return []
    return subscriptions.filter((plan) => {
      if (!plan) return false
      const employerMatch = plan?.employer?.name?.toLowerCase().includes((searchTerm || "").toLowerCase())
      const amountMatch = plan.amount?.toString().includes(searchTerm || "")
      const statusMatch = plan.status?.toLowerCase().includes((searchTerm || "").toLowerCase())
      const subscriptionTitleMatch = employerSubscriptionOptions
        .find((sub) => sub.uuid === plan.subscription_uuid)
        ?.title?.toLowerCase()
        .includes((searchTerm || "").toLowerCase())
      return employerMatch || amountMatch || statusMatch || subscriptionTitleMatch
    })
  }, [subscriptions, searchTerm, employerSubscriptionOptions])

  const handleEmployerSelect = (user) => {
    setCurrentPlan({
      ...currentPlan,
      user_uuid: user.uuid,
    })
    setSelectedEmployerName(user.name || user.email)
  }

  const handleSubscriptionSelect = (e) => {
    const selectedUuid = e.target.value
    setCurrentPlan((prevPlan) => ({
      ...prevPlan,
      subscription_uuid: selectedUuid,
    }))
    const selectedSub = employerSubscriptionOptions.find((sub) => sub.uuid === selectedUuid)
    setSelectedSubscriptionTitle(selectedSub ? `${selectedSub.title} - ₹${selectedSub.amount}` : "")
  }

  const handleScroll = () => {
    if (menuListRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = menuListRef.current
      if (
        scrollTop + clientHeight >= scrollHeight - 50 && // 50px buffer
        employerDropdownPagination.hasMore &&
        !isFetchingMoreEmployers
      ) {
        dispatch(fetchEmployersForDropdownAsync(employerDropdownPagination.currentPage + 1))
      }
    }
  }

  const renderPlanForm = () => (
    <VStack spacing={4}>
      {currentPlan?.employer && isEditOpen ? (
        <FormControl>
          <FormLabel>Employer</FormLabel>
          <Input value={currentPlan?.employer.name || currentPlan?.employer.email} className="capitalize" isReadOnly />
        </FormControl>
      ) : (
        <FormControl>
          <FormLabel>User</FormLabel>
          <Menu>
            <MenuButton as={Button} rightIcon={<ChevronDownIcon />} width="100%">
              {selectedEmployerName || "Select a user"}
            </MenuButton>
            <MenuList maxH="200px" overflowY="auto" ref={menuListRef} onScroll={handleScroll}>
              {allEmployers.length === 0 && !isFetchingMoreEmployers ? (
                <MenuItem isDisabled>No employers found.</MenuItem>
              ) : (
                allEmployers.map((user) => (
                  <MenuItem key={user.uuid} onClick={() => handleEmployerSelect(user)}>
                    <div className="gap-2 flex justify-between p-2 bg-gray-50 rounded-md">
                      <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-md font-medium">
                        {user?.name}
                      </span>
                      <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-md font-medium">
                        {user?.email}
                      </span>
                    </div>
                  </MenuItem>
                ))
              )}
              {isFetchingMoreEmployers && (
                <MenuItem isDisabled>
                  <Flex align="center" justify="center" width="100%">
                    <Spinner size="sm" mr={2} /> Loading more...
                  </Flex>
                </MenuItem>
              )}
              {!employerDropdownPagination.hasMore && allEmployers.length > 0 && (
                <MenuItem isDisabled>No more employers</MenuItem>
              )}
            </MenuList>
          </Menu>
        </FormControl>
      )}

      {/* New Subscription Plan Select */}
      <FormControl>
        <FormLabel>Subscription Plan</FormLabel>
        <Select
          name="subscription_uuid"
          value={currentPlan.subscription_uuid}
          onChange={handleSubscriptionSelect}
          placeholder="Select a subscription plan"
        >
          {employerSubscriptionOptions.map((sub) => (
            <option key={sub.uuid} value={sub.uuid}>
              {sub.title} - ₹{sub.amount}
            </option>
          ))}
        </Select>
      </FormControl>

      <FormControl>
        <FormLabel>Amount</FormLabel>
        <NumberInput min={0}>
          <NumberInputField
            name="amount"
            value={currentPlan?.amount ?? ""}
            onChange={(e) => handleNumberInputChange("amount", e.target.value)}
          />
        </NumberInput>
      </FormControl>
      <HStack width="100%">
        <FormControl>
          <FormLabel>Validity</FormLabel>
          <NumberInput min={1}>
            <NumberInputField
              name="validity"
              value={currentPlan.validity}
              onChange={(e) => handleNumberInputChange("validity", e.target.value)}
            />
          </NumberInput>
        </FormControl>
        <FormControl>
          <FormLabel>Validity Type</FormLabel>
          <Select name="validity_type" value={currentPlan.validity_type} onChange={handleInputChange}>
            <option value="day">Day</option>
            <option value="month">Month</option>
            <option value="year">Year</option>
          </Select>
        </FormControl>
      </HStack>
      <FormControl>
        <FormLabel>Status</FormLabel>
        <Select name="status" value={currentPlan.status} onChange={handleInputChange}>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </Select>
      </FormControl>
      <FormControl>
        <FormLabel>Profile Creation</FormLabel>
        <Switch isChecked={currentPlan.profile_creation} onChange={() => handleSwitchChange("profile_creation")} />
      </FormControl>
      <FormControl>
        <FormLabel>Notification via Email/SMS/WhatsApp</FormLabel>
        <Switch
          isChecked={currentPlan.notification_via_email_sms_whatsapp}
          onChange={() => handleSwitchChange("notification_via_email_sms_whatsapp")}
        />
      </FormControl>
      <FormControl>
        <FormLabel>Number of Job Posts</FormLabel>
        <NumberInput min={0}>
          <NumberInputField
            name="no_of_job_post"
            value={currentPlan.no_of_job_post}
            onChange={(e) => handleNumberInputChange("no_of_job_post", e.target.value)}
          />
        </NumberInput>
      </FormControl>
      <FormControl>
        <FormLabel>Number of Urgent Hiring Tags</FormLabel>
        <NumberInput min={0}>
          <NumberInputField
            name="no_of_urgent_hiring_tag"
            value={currentPlan.no_of_urgent_hiring_tag}
            onChange={(e) => handleNumberInputChange("no_of_urgent_hiring_tag", e.target.value)}
          />
        </NumberInput>
      </FormControl>
      <FormControl>
        <FormLabel>Number of Candidate Unlocks</FormLabel>
        <NumberInput min={0}>
          <NumberInputField
            name="no_of_candidate_unlock"
            value={currentPlan.no_of_candidate_unlock}
            onChange={(e) => handleNumberInputChange("no_of_candidate_unlock", e.target.value)}
          />
        </NumberInput>
      </FormControl>
      <FormControl>
        <FormLabel>Number of Job Published</FormLabel>
        <NumberInput min={0}>
          <NumberInputField
            name="no_of_job_published"
            value={currentPlan.no_of_job_published}
            onChange={(e) => handleNumberInputChange("no_of_job_published", e.target.value)}
          />
        </NumberInput>
      </FormControl>
      <FormControl>
        <FormLabel>Job Boosts</FormLabel>
        <Switch isChecked={currentPlan.job_boosts} onChange={() => handleSwitchChange("job_boosts")} />
      </FormControl>
      <FormControl>
        <FormLabel>Smart Boost via Email/SMS/WhatsApp</FormLabel>
        <Switch
          isChecked={currentPlan.smart_boost_via_email_sms_whatsapp}
          onChange={() => handleSwitchChange("smart_boost_via_email_sms_whatsapp")}
        />
      </FormControl>
      <FormControl>
        <FormLabel>Dedicated Relationship Manager</FormLabel>
        <Switch
          isChecked={currentPlan.dedicated_relationship_manager}
          onChange={() => handleSwitchChange("dedicated_relationship_manager")}
        />
      </FormControl>
      <FormControl>
        <FormLabel>Campaign via Email/SMS/WhatsApp</FormLabel>
        <Switch
          isChecked={currentPlan.campion_via_email_sms_whatsapp}
          onChange={() => handleSwitchChange("campion_via_email_sms_whatsapp")}
        />
      </FormControl>
      <FormControl>
        <FormLabel>Multiple User Login</FormLabel>
        <Switch
          isChecked={currentPlan.multiple_user_login}
          onChange={() => handleSwitchChange("multiple_user_login")}
        />
      </FormControl>
      <FormControl>
        <FormLabel>Reports</FormLabel>
        <Switch isChecked={currentPlan.reports} onChange={() => handleSwitchChange("reports")} />
      </FormControl>
      <FormControl>
        <FormLabel>Company Branding</FormLabel>
        <Switch isChecked={currentPlan.company_branding} onChange={() => handleSwitchChange("company_branding")} />
      </FormControl>
      {/* New fields */}
      <FormControl>
        <FormLabel>Job Applicants Download</FormLabel>
        <Switch
          isChecked={currentPlan.job_applicants_download}
          onChange={() => handleSwitchChange("job_applicants_download")}
        />
      </FormControl>
      <FormControl>
        <FormLabel>Unlocked Candidate Download</FormLabel>
        <Switch
          isChecked={currentPlan.unlocked_candidate_download}
          onChange={() => handleSwitchChange("unlocked_candidate_download")}
        />
      </FormControl>
    </VStack>
  )

  return (
    <div className="bg-adminbg rounded" style={{ fontFamily: "Inter, sans-serif" }}>
      <Container maxW="container.2xl" className="bg-white my-2 rounded-md">
        <Flex justifyContent="space-between" alignItems="center" mb={6}>
          <h2 className="text-xl text-black font-semibold px-3 py-7">Subscription Plans Custom</h2>
          <Button
            leftIcon={<AddIcon />}
            colorScheme="blue"
            onClick={() => {
              setCurrentPlan(initialPlanState)
              setSelectedEmployerName("")
              setSelectedSubscriptionTitle("") // Reset selected subscription title when opening add modal
              onAddOpen()
            }}
          >
            Add New Plan
          </Button>
        </Flex>
        <VStack spacing={4} mb={8}>
          <HStack width="100%" spacing={4}>
            <Input placeholder="Search plans..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
          </HStack>
        </VStack>
        <TableContainer>
          <Table colorScheme="gray" size="sm">
            <Thead className="bg-gray-200">
              <Tr className="">
                <Th width="40px">
                  <Checkbox
                    isChecked={selectedPlans.length === filteredPlans.length && filteredPlans.length > 0}
                    onChange={() => {
                      if (selectedPlans.length === filteredPlans.length) {
                        setSelectedPlans([])
                      } else {
                        setSelectedPlans(filteredPlans.map((plan) => plan.uuid))
                      }
                    }}
                  />
                </Th>
                <Th>User</Th>
                <Th>Subscription Plan</Th> {/* Added new column */}
                <Th>Amount</Th>
                <Th>Validity</Th>
                <Th>Status</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredPlans.map((plan) => (
                <Tr key={plan.uuid}>
                  <Td>
                    <Checkbox
                      isChecked={selectedPlans.includes(plan.uuid)}
                      onChange={() => togglePlanSelection(plan.uuid)}
                    />
                  </Td>
                  <Td fontWeight="medium capitalize">{plan?.employer?.name || plan?.employer?.email || "Unknown"}</Td>
                  <Td>
                    {employerSubscriptionOptions.find((sub) => sub.uuid === plan.subscription_uuid)?.title || "N/A"}
                  </Td>
                  <Td>₹{plan.amount}</Td>
                  <Td>
                    {plan.validity} {plan.validity_type}
                    {plan.validity !== 1 ? "s" : ""}
                  </Td>
                  <Td>
                    <Badge colorScheme={plan.status === "active" ? "green" : "red"}>{plan.status}</Badge>
                  </Td>
                  <Td>
                    <HStack spacing={2}>
                      <IconButton
                        aria-label="View plan"
                        icon={<ViewIcon />}
                        size="sm"
                        colorScheme="blue"
                        variant="outline"
                        onClick={() => handleViewPlan(plan)}
                      />
                      <IconButton
                        aria-label="Edit plan"
                        icon={<EditIcon />}
                        size="sm"
                        colorScheme="green"
                        variant="outline"
                        onClick={() => handleEditPlan(plan)}
                      />
                      <IconButton
                        aria-label="Delete plan"
                        icon={<DeleteIcon />}
                        size="sm"
                        colorScheme="red"
                        variant="outline"
                        onClick={() => handleDeleteConfirmation(plan.uuid)}
                      />
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </Container>
      {/* Add Plan Modal */}
      <Modal isOpen={isAddOpen} onClose={onAddClose} size="5xl" isCentered>
        <ModalOverlay />
        <ModalContent maxHeight={"80vh"}>
          <ModalHeader>Add New Plan</ModalHeader>
          <ModalCloseButton />
          <ModalBody overflowY="auto">{renderPlanForm()}</ModalBody>
          <ModalFooter className="bg-textBlue">
            <Button colorScheme="blue" mr={3} onClick={handleAddPlan}>
              Add
            </Button>
            <Button variant="ghost" onClick={onAddClose}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {/* Edit Plan Modal */}
      <Modal isOpen={isEditOpen} onClose={onEditClose} size="5xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Plan</ModalHeader>
          <ModalCloseButton />
          <ModalBody overflowY="auto" maxHeight="calc(100vh - 200px)">
            {renderPlanForm()}
          </ModalBody>
          <ModalFooter className="bg-textBlue">
            <Button colorScheme="blue" mr={3} onClick={handleUpdatePlan}>
              Update
            </Button>
            <Button variant="ghost" onClick={onEditClose}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {/* Delete Plan Modal */}
      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Plan</ModalHeader>
          <ModalCloseButton />
          <ModalBody>Are you sure you want to delete this plan?</ModalBody>
          <ModalFooter>
            <Button colorScheme="red" mr={3} onClick={handleDeletePlan}>
              Delete
            </Button>
            <Button variant="ghost" onClick={onDeleteClose}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {/* View Plan Modal */}
      <Modal isOpen={isViewOpen} onClose={onViewClose} size="5xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>View Plan Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody overflowY="auto" maxHeight="calc(100vh - 200px)">
            <VStack align="start" spacing={4}>
              <Text>
                <strong>User:</strong>{" "}
                {allEmployers.find((user) => user.uuid === currentPlan.user_uuid)?.name ||
                  currentPlan?.employer?.name ||
                  "Unknown"}
              </Text>
              <Text>
                <strong>Subscription Plan:</strong>{" "}
                {employerSubscriptionOptions.find((sub) => sub.uuid === currentPlan.subscription_uuid)?.title || "N/A"}
              </Text>
              <Text>
                <strong>Amount:</strong> ₹{currentPlan.amount}
              </Text>
              <Text>
                <strong>Validity:</strong> {currentPlan.validity} {currentPlan.validity_type}
                {currentPlan.validity !== 1 ? "s" : ""}
              </Text>
              <Text>
                <strong>Status:</strong> {currentPlan.status}
              </Text>
              <Text>
                <strong>Profile Creation:</strong> {currentPlan.profile_creation ? "Yes" : "No"}
              </Text>
              <Text>
                <strong>Notification via Email/SMS/WhatsApp:</strong>{" "}
                {currentPlan.notification_via_email_sms_whatsapp ? "Yes" : "No"}
              </Text>
              <Text>
                <strong>Number of Job Posts:</strong> {currentPlan.no_of_job_post}
              </Text>
              <Text>
                <strong>Number of Urgent Hiring Tags:</strong> {currentPlan.no_of_urgent_hiring_tag}
              </Text>
              <Text>
                <strong>Number of Candidate Unlocks:</strong> {currentPlan.no_of_candidate_unlock}
              </Text>
              <Text>
                <strong>Number of Job Published:</strong> {currentPlan.no_of_job_published}
              </Text>
              <Text>
                <strong>Job Boosts:</strong> {currentPlan.job_boosts ? "Yes" : "No"}
              </Text>
              <Text>
                <strong>Smart Boost via Email/SMS/WhatsApp:</strong>{" "}
                {currentPlan.smart_boost_via_email_sms_whatsapp ? "Yes" : "No"}
              </Text>
              <Text>
                <strong>Dedicated Relationship Manager:</strong>{" "}
                {currentPlan.dedicated_relationship_manager ? "Yes" : "No"}
              </Text>
              <Text>
                <strong>Campaign via Email/SMS/WhatsApp:</strong>{" "}
                {currentPlan.campion_via_email_sms_whatsapp ? "Yes" : "No"}
              </Text>
              <Text>
                <strong>Multiple User Login:</strong> {currentPlan.multiple_user_login ? "Yes" : "No"}
              </Text>
              <Text>
                <strong>Reports:</strong> {currentPlan.reports ? "Yes" : "No"}
              </Text>
              <Text>
                <strong>Company Branding:</strong> {currentPlan.company_branding ? "Yes" : "No"}
              </Text>
              {/* New fields for view modal */}
              <Text>
                <strong>Job Applicants Download:</strong> {currentPlan.job_applicants_download ? "Yes" : "No"}
              </Text>
              <Text>
                <strong>Unlocked Candidate Download:</strong> {currentPlan.unlocked_candidate_download ? "Yes" : "No"}
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={onViewClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  )
}

export default SubscriptionCustom






// "use client";

// import { useState, useMemo, useEffect } from "react";
// import {
//   Button,
//   Modal,
//   ModalOverlay,
//   ModalContent,
//   ModalHeader,
//   ModalCloseButton,
//   ModalBody,
//   ModalFooter,
//   Input,
//   Select,
//   Switch,
//   useDisclosure,
//   useToast,
//   FormControl,
//   FormLabel,
//   NumberInput,
//   NumberInputField,
//   VStack,
//   HStack,
//   Text,
//   Table,
//   Thead,
//   Tbody,
//   Tr,
//   Th,
//   Td,
//   Badge,
//   Flex,
//   IconButton,
//   Container,
//   TableContainer,
//   Checkbox,
// } from "@chakra-ui/react";
// import { AddIcon, ViewIcon, EditIcon, DeleteIcon } from "@chakra-ui/icons";
// import { useDispatch, useSelector } from "react-redux";
// import {
//   addSubscriptionCustomAsync,
//   deleteSubscriptionCustomAsync,
//   fetchSubscriptionCustomAsync,
//   selectSubscriptionCustom,
//   updateSubscriptionCustomAsync,
// } from "../../Subscriptions/SubscriptionCustom/SubscriptionCustomSlice";
// import { fetchEmployerSubscription } from "../../Subscriptions/SubscriptionDetails/SubscriptionAPI";
// import {
//   fetchEmployerSubscriptionasync,
//   selectSubscription,
// } from "../../Subscriptions/SubscriptionDetails/SubscriptionSlice";
// import {
//   fetchAllEmployersAsync,
//   selectAllEmployers
// } from "../../EmployerData/EmployerDataSlice";

// const initialPlanState = {
//   user_uuid: "",
//   subscription_uuid: "",
//   amount: "",
//   validity: "",
//   validity_type: "day",
//   status: "active",
//   profile_creation: false,
//   notification_via_email_sms_whatsapp: false,
//   no_of_job_post: 0,
//   no_of_urgent_hiring_tag: 0,
//   no_of_candidate_unlock: 0,
//   no_of_job_published: 0,
//   job_boosts: false,
//   smart_boost_via_email_sms_whatsapp: false,
//   dedicated_relationship_manager: false,
//   campion_via_email_sms_whatsapp: false,
//   multiple_user_login: false,
//   reports: false,
//   company_branding: false,
// };

// const SubscriptionCustom = () => {
//   const dispatch = useDispatch();
//   const subscriptions = useSelector(selectSubscriptionCustom);
//   const employerSubscriptions = useSelector(selectSubscription);
//   const allEmployers = useSelector(selectAllEmployers); // Use all employers
//   const [currentPlan, setCurrentPlan] = useState(initialPlanState);
//   const [selectedPlans, setSelectedPlans] = useState([]);
//   const [searchTerm, setSearchTerm] = useState("");

//   const {
//     isOpen: isAddOpen,
//     onOpen: onAddOpen,
//     onClose: onAddClose,
//   } = useDisclosure();
//   const {
//     isOpen: isEditOpen,
//     onOpen: onEditOpen,
//     onClose: onEditClose,
//   } = useDisclosure();
//   const {
//     isOpen: isDeleteOpen,
//     onOpen: onDeleteOpen,
//     onClose: onDeleteClose,
//   } = useDisclosure();
//   const {
//     isOpen: isViewOpen,
//     onOpen: onViewOpen,
//     onClose: onViewClose,
//   } = useDisclosure();

//   const toast = useToast();

//   useEffect(() => {
//     dispatch(fetchSubscriptionCustomAsync());
//     dispatch(fetchEmployerSubscriptionasync());
//     dispatch(fetchAllEmployersAsync()); // Fetch all employers for dropdowns
//   }, [dispatch]);

//   const handleEditPlan = (plan) => {
//     setCurrentPlan({
//       ...plan,
//       amount: plan.amount || 0,
//       validity: plan.validity || 1,
//       no_of_job_post: plan.no_of_job_post || 0,
//       no_of_urgent_hiring_tag: plan.no_of_urgent_hiring_tag || 0,
//       no_of_candidate_unlock: plan.no_of_candidate_unlock || 0,
//       no_of_job_published: plan.no_of_job_published || 0,
//     });
//     console.log("current", currentPlan)
//     onEditOpen();
//   };

//   useEffect(() => {
//     console.log("Updated currentPlan:", currentPlan);
//   }, [currentPlan]);

//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setCurrentPlan((prevPlan) => ({
//       ...prevPlan,
//       [name]: value || "", // Ensures no `undefined` values
//     }));
//   };

//   const handleSwitchChange = (name) => {
//     setCurrentPlan((prevPlan) => ({
//       ...prevPlan,
//       [name]: !prevPlan[name], // Toggle boolean values correctly
//     }));
//   };

//   const handleNumberInputChange = (name, value) => {
//     setCurrentPlan((prevPlan) => ({
//       ...prevPlan,
//       [name]: Number(value) || 0, // Convert to number before updating state
//     }));
//   };


//   const handleAddPlan = async () => {
//     try {
//       const newPlan = {
//         ...currentPlan,
//         subscription_uuid: "1538bb16-98ad-4bae-be0a-f8f61edd165a", // static for now
//         amount: Number.parseFloat(currentPlan.amount),
//         validity: Number.parseInt(currentPlan.validity),
//         no_of_job_post: Number.parseInt(currentPlan.no_of_job_post),
//         no_of_urgent_hiring_tag: Number.parseInt(
//           currentPlan.no_of_urgent_hiring_tag
//         ),
//         no_of_candidate_unlock: Number.parseInt(
//           currentPlan.no_of_candidate_unlock
//         ),
//         no_of_job_published: Number.parseInt(currentPlan.no_of_job_published),
//       };
//       console.log("new plan", newPlan);
//       await dispatch(addSubscriptionCustomAsync(newPlan)).unwrap();
//       toast({
//         title: "Plan added successfully.",
//         status: "success",
//         duration: 2000,
//         isClosable: true,
//       });
//       setCurrentPlan(initialPlanState);
//       onAddClose();
//       dispatch(fetchSubscriptionCustomAsync());
//     } catch (error) {
//       toast({
//         title: "Failed to add plan.",
//         description: error.toString(),
//         status: "error",
//         duration: 2000,
//         isClosable: true,
//       });
//     }
//   };


//   const handleUpdatePlan = async () => {
//     try {
//       const updatedPlan = {
//         ...currentPlan,
//         subscription_uuid: "1538bb16-98ad-4bae-be0a-f8f61edd165a", // static for now

//         amount: Number.parseFloat(currentPlan.amount),
//         validity: Number.parseInt(currentPlan.validity),
//         no_of_job_post: Number.parseInt(currentPlan.no_of_job_post),
//         no_of_urgent_hiring_tag: Number.parseInt(
//           currentPlan.no_of_urgent_hiring_tag
//         ),
//         no_of_candidate_unlock: Number.parseInt(
//           currentPlan.no_of_candidate_unlock
//         ),
//         no_of_job_published: Number.parseInt(currentPlan.no_of_job_published),
//       };
//       console.log("current", currentPlan);
//       await dispatch(
//         updateSubscriptionCustomAsync({
//           id: currentPlan.uuid,
//           updateSubscriptiondata: updatedPlan,
//         })
//       ).unwrap();
//       toast({
//         title: "Plan updated successfully.",
//         status: "success",
//         duration: 2000,
//         isClosable: true,
//       });
//       setCurrentPlan(initialPlanState);
//       onEditClose();
//       dispatch(fetchSubscriptionCustomAsync());
//     } catch (error) {
//       toast({
//         title: "Failed to update plan.",
//         description: error.toString(),
//         status: "error",
//         duration: 2000,
//         isClosable: true,
//       });
//     }
//   };

//   const handleDeletePlan = async () => {
//     try {
//       await dispatch(
//         deleteSubscriptionCustomAsync(currentPlan.uuid)
//       ).unwrap();
//       toast({
//         title: "Plan deleted successfully.",
//         status: "success",
//         duration: 2000,
//         isClosable: true,
//       });
//       setCurrentPlan(initialPlanState);
//       onDeleteClose();
//       dispatch(fetchSubscriptionCustomAsync());
//     } catch (error) {
//       toast({
//         title: "Failed to delete plan.",
//         description: error.toString(),
//         status: "error",
//         duration: 2000,
//         isClosable: true,
//       });
//     }
//   };


//   const handleDeleteConfirmation = (planId) => {
//     const planToDelete = subscriptions.find(
//       (plan) => plan.subscription_uuid === planId
//     );
//     if (planToDelete) {
//       setCurrentPlan(planToDelete);
//       onDeleteOpen();
//     }
//   };

//   const handleViewPlan = (plan) => {
//     setCurrentPlan(plan);
//     onViewOpen();
//   };

//   const togglePlanSelection = (planId) => {
//     if (selectedPlans.includes(planId)) {
//       setSelectedPlans(selectedPlans.filter((id) => id !== planId));
//     } else {
//       setSelectedPlans([...selectedPlans, planId]);
//     }
//   };

//   // Filter plans based on search term
//   const filteredPlans = useMemo(() => {
//     if (!Array.isArray(subscriptions)) return []

//     return subscriptions.filter((plan) => {
//       if (!plan) return false

//       const employerMatch = plan?.employer?.name?.toLowerCase().includes((searchTerm || "").toLowerCase())
//       const amountMatch = plan.amount?.toString().includes(searchTerm || "")
//       const statusMatch = plan.status?.toLowerCase().includes((searchTerm || "").toLowerCase())

//       return employerMatch || amountMatch || statusMatch
//     })
//   }, [subscriptions, searchTerm])

//   const renderPlanForm = () => (
//     <VStack spacing={4}>
//       {currentPlan?.employer ? ( // If editing, show employer name instead of user select dropdown
//         <FormControl>
//           <FormLabel>Employer</FormLabel>
//           <Input
//             value={currentPlan?.employer.name}
//             className="capitalize"
//             isReadOnly
//           />
//         </FormControl>
//       ) : (
//         // If adding, show the user dropdown with all employers
//         <FormControl>
//           <FormLabel>User</FormLabel>
//           <Select
//             name="user_uuid"
//             value={currentPlan.user_uuid}
//             onChange={handleInputChange}
//           >
//             <option value="">Select a user</option>
//             {allEmployers.map((user) => (
//               <option key={user.uuid} value={user.uuid}>
//                 {user.email || user.email}
//               </option>
//             ))}
//           </Select>
//         </FormControl>
//       )}

//       <FormControl>
//         <FormLabel>Amount</FormLabel>
//         <NumberInput min={0}>
//           <NumberInputField
//             name="amount"
//             value={currentPlan?.amount ?? ""}
//             onChange={(e) => handleNumberInputChange("amount", e.target.value)}
//           />
//         </NumberInput>
//       </FormControl>

//       <HStack width="100%">
//         <FormControl>
//           <FormLabel>Validity</FormLabel>
//           <NumberInput min={1}>
//             <NumberInputField
//               name="validity"
//               value={currentPlan.validity}
//               onChange={(e) =>
//                 handleNumberInputChange("validity", e.target.value)
//               }
//             />
//           </NumberInput>
//         </FormControl>

//         <FormControl>
//           <FormLabel>Validity Type</FormLabel>
//           <Select
//             name="validity_type"
//             value={currentPlan.validity_type}
//             onChange={handleInputChange}
//           >
//             <option value="day">Day</option>
//             <option value="month">Month</option>
//             <option value="year">Year</option>
//           </Select>
//         </FormControl>
//       </HStack>

//       <FormControl>
//         <FormLabel>Status</FormLabel>
//         <Select
//           name="status"
//           value={currentPlan.status}
//           onChange={handleInputChange}
//         >
//           <option value="active">Active</option>
//           <option value="inactive">Inactive</option>
//         </Select>
//       </FormControl>

//       <FormControl>
//         <FormLabel>Profile Creation</FormLabel>
//         <Switch
//           isChecked={currentPlan.profile_creation}
//           onChange={() => handleSwitchChange("profile_creation")}
//         />
//       </FormControl>

//       <FormControl>
//         <FormLabel>Notification via Email/SMS/WhatsApp</FormLabel>
//         <Switch
//           isChecked={currentPlan.notification_via_email_sms_whatsapp}
//           onChange={() =>
//             handleSwitchChange("notification_via_email_sms_whatsapp")
//           }
//         />
//       </FormControl>

//       <FormControl>
//         <FormLabel>Number of Job Posts</FormLabel>
//         <NumberInput min={0}>
//           <NumberInputField
//             name="no_of_job_post"
//             value={currentPlan.no_of_job_post}
//             onChange={(e) =>
//               handleNumberInputChange("no_of_job_post", e.target.value)
//             }
//           />
//         </NumberInput>
//       </FormControl>

//       <FormControl>
//         <FormLabel>Number of Urgent Hiring Tags</FormLabel>
//         <NumberInput min={0}>
//           <NumberInputField
//             name="no_of_urgent_hiring_tag"
//             value={currentPlan.no_of_urgent_hiring_tag}
//             onChange={(e) =>
//               handleNumberInputChange("no_of_urgent_hiring_tag", e.target.value)
//             }
//           />
//         </NumberInput>
//       </FormControl>

//       <FormControl>
//         <FormLabel>Number of Candidate Unlocks</FormLabel>
//         <NumberInput min={0}>
//           <NumberInputField
//             name="no_of_candidate_unlock"
//             value={currentPlan.no_of_candidate_unlock}
//             onChange={(e) =>
//               handleNumberInputChange("no_of_candidate_unlock", e.target.value)
//             }
//           />
//         </NumberInput>
//       </FormControl>

//       <FormControl>
//         <FormLabel>Number of Job Published</FormLabel>
//         <NumberInput min={0}>
//           <NumberInputField
//             name="no_of_job_published"
//             value={currentPlan.no_of_job_published}
//             onChange={(e) =>
//               handleNumberInputChange("no_of_job_published", e.target.value)
//             }
//           />
//         </NumberInput>
//       </FormControl>

//       <FormControl>
//         <FormLabel>Job Boosts</FormLabel>
//         <Switch
//           isChecked={currentPlan.job_boosts}
//           onChange={() => handleSwitchChange("job_boosts")}
//         />
//       </FormControl>

//       <FormControl>
//         <FormLabel>Smart Boost via Email/SMS/WhatsApp</FormLabel>
//         <Switch
//           isChecked={currentPlan.smart_boost_via_email_sms_whatsapp}
//           onChange={() =>
//             handleSwitchChange("smart_boost_via_email_sms_whatsapp")
//           }
//         />
//       </FormControl>

//       <FormControl>
//         <FormLabel>Dedicated Relationship Manager</FormLabel>
//         <Switch
//           isChecked={currentPlan.dedicated_relationship_manager}
//           onChange={() => handleSwitchChange("dedicated_relationship_manager")}
//         />
//       </FormControl>

//       <FormControl>
//         <FormLabel>Campaign via Email/SMS/WhatsApp</FormLabel>
//         <Switch
//           isChecked={currentPlan.campion_via_email_sms_whatsapp}
//           onChange={() => handleSwitchChange("campion_via_email_sms_whatsapp")}
//         />
//       </FormControl>

//       <FormControl>
//         <FormLabel>Multiple User Login</FormLabel>
//         <Switch
//           isChecked={currentPlan.multiple_user_login}
//           onChange={() => handleSwitchChange("multiple_user_login")}
//         />
//       </FormControl>

//       <FormControl>
//         <FormLabel>Reports</FormLabel>
//         <Switch
//           isChecked={currentPlan.reports}
//           onChange={() => handleSwitchChange("reports")}
//         />
//       </FormControl>

//       <FormControl>
//         <FormLabel>Company Branding</FormLabel>
//         <Switch
//           isChecked={currentPlan.company_branding}
//           onChange={() => handleSwitchChange("company_branding")}
//         />
//       </FormControl>
//     </VStack>
//   );

//   return (
//     <div
//       className="bg-adminbg rounded  "
//       style={{ fontFamily: "Inter, sans-serif" }}
//     >
//       <Container maxW="container.2xl" className="bg-white my-2 rounded-md">
//         <Flex justifyContent="space-between" alignItems="center" mb={6}>
//           <h2 className="text-xl text-black font-semibold px-3 py-7">
//             Subscription Plans Custom
//           </h2>
//           <Button
//             leftIcon={<AddIcon />}
//             colorScheme="blue"
//             onClick={() => {
//               setCurrentPlan(initialPlanState);
//               onAddOpen();
//             }}
//           >
//             Add New Plan
//           </Button>
//         </Flex>

//         <VStack spacing={4} mb={8}>
//           <HStack width="100%" spacing={4}>
//             <Input
//               placeholder="Search plans..."
//               value={searchTerm}
//               onChange={(e) => setSearchTerm(e.target.value)}
//             />
//           </HStack>
//         </VStack>

//         <TableContainer>
//           <Table colorScheme="gray" size="sm">
//             <Thead className="bg-gray-200">
//               <Tr className="">
//                 <Th width="40px">
//                   <Checkbox
//                     isChecked={
//                       selectedPlans.length === filteredPlans.length &&
//                       filteredPlans.length > 0
//                     }
//                     onChange={() => {
//                       if (selectedPlans.length === filteredPlans.length) {
//                         setSelectedPlans([])
//                       } else {
//                         setSelectedPlans(filteredPlans.map((plan) => plan.subscription_uuid))
//                       }
//                     }}
//                   />
//                 </Th>
//                 <Th>User</Th>
//                 <Th>Amount</Th>
//                 <Th>Validity</Th>
//                 <Th>Status</Th>
//                 <Th>Actions</Th>
//               </Tr>
//             </Thead>
//             <Tbody>
//               {filteredPlans.map((plan) => (
//                 <Tr key={plan.subscription_uuid}>
//                   <Td>
//                     <Checkbox
//                       isChecked={selectedPlans.includes(plan.subscription_uuid)}
//                       onChange={() =>
//                         togglePlanSelection(plan.subscription_uuid)
//                       }
//                     />
//                   </Td>
//                   <Td fontWeight="medium capitalize">
//                     {plan?.employer?.name || "Unknown"}
//                   </Td>
//                   <Td>₹{plan.amount}</Td>
//                   <Td>
//                     {plan.validity} {plan.validity_type}
//                     {plan.validity !== 1 ? "s" : ""}
//                   </Td>
//                   <Td>
//                     <Badge
//                       colorScheme={plan.status === "active" ? "green" : "red"}
//                     >
//                       {plan.status}
//                     </Badge>
//                   </Td>
//                   <Td>
//                     <HStack spacing={2}>
//                       <IconButton
//                         aria-label="View plan"
//                         icon={<ViewIcon />}
//                         size="sm"
//                         colorScheme="blue"
//                         variant="outline"
//                         onClick={() => handleViewPlan(plan)}
//                       />
//                       <IconButton
//                         aria-label="Edit plan"
//                         icon={<EditIcon />}
//                         size="sm"
//                         colorScheme="green"
//                         variant="outline"
//                         onClick={() => handleEditPlan(plan)}
//                       />
//                       <IconButton
//                         aria-label="Delete plan"
//                         icon={<DeleteIcon />}
//                         size="sm"
//                         colorScheme="red"
//                         variant="outline"
//                         onClick={() =>
//                           handleDeleteConfirmation(plan.subscription_uuid)
//                         }
//                       />
//                     </HStack>
//                   </Td>
//                 </Tr>
//               ))}
//             </Tbody>
//           </Table>
//         </TableContainer>

//       </Container>
//       {/* Add Plan Modal */}
//       <Modal isOpen={isAddOpen} onClose={onAddClose} size="5xl" isCentered>
//         <ModalOverlay />
//         <ModalContent maxHeight={"80vh"}>
//           <ModalHeader>Add New Plan</ModalHeader>
//           <ModalCloseButton />
//           <ModalBody overflowY="auto">{renderPlanForm()}</ModalBody>
//           <ModalFooter className="bg-textBlue">
//             <Button colorScheme="blue" mr={3} onClick={handleAddPlan}>
//               Add
//             </Button>
//             <Button variant="ghost" onClick={onAddClose}>
//               Cancel
//             </Button>
//           </ModalFooter>
//         </ModalContent>
//       </Modal>

//       {/* Edit Plan Modal */}
//       <Modal isOpen={isEditOpen} onClose={onEditClose} size="5xl">
//         <ModalOverlay />
//         <ModalContent>
//           <ModalHeader>Edit Plan</ModalHeader>
//           <ModalCloseButton />
//           <ModalBody overflowY="auto" maxHeight="calc(100vh - 200px)">
//             {renderPlanForm()}
//           </ModalBody>
//           <ModalFooter className="bg-textBlue">
//             <Button colorScheme="blue" mr={3} onClick={handleUpdatePlan}>
//               Update
//             </Button>
//             <Button variant="ghost" onClick={onEditClose}>
//               Cancel
//             </Button>
//           </ModalFooter>
//         </ModalContent>
//       </Modal>

//       {/* Delete Plan Modal */}
//       <Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
//         <ModalOverlay />
//         <ModalContent>
//           <ModalHeader>Delete Plan</ModalHeader>
//           <ModalCloseButton />
//           <ModalBody>Are you sure you want to delete this plan?</ModalBody>
//           <ModalFooter>
//             <Button colorScheme="red" mr={3} onClick={handleDeletePlan}>
//               Delete
//             </Button>
//             <Button variant="ghost" onClick={onDeleteClose}>
//               Cancel
//             </Button>
//           </ModalFooter>
//         </ModalContent>
//       </Modal>

//       {/* View Plan Modal */}
//       <Modal isOpen={isViewOpen} onClose={onViewClose} size="5xl">
//         <ModalOverlay />
//         <ModalContent>
//           <ModalHeader>View Plan Details</ModalHeader>
//           <ModalCloseButton />
//           <ModalBody overflowY="auto" maxHeight="calc(100vh - 200px)">
//             <VStack align="start" spacing={4}>
//               <Text>
//                 <strong>User:</strong>{" "}
//                 {allEmployers.find((user) => user.uuid === currentPlan.user_uuid)
//                   ?.name || currentPlan?.employer?.name || "Unknown"}
//               </Text>
//               <Text>
//                 <strong>Amount:</strong> ₹{currentPlan.amount}
//               </Text>
//               <Text>
//                 <strong>Validity:</strong> {currentPlan.validity}{" "}
//                 {currentPlan.validity_type}
//                 {currentPlan.validity !== 1 ? "s" : ""}
//               </Text>
//               <Text>
//                 <strong>Status:</strong> {currentPlan.status}
//               </Text>
//               <Text>
//                 <strong>Profile Creation:</strong>{" "}
//                 {currentPlan.profile_creation ? "Yes" : "No"}
//               </Text>
//               <Text>
//                 <strong>Notification via Email/SMS/WhatsApp:</strong>{" "}
//                 {currentPlan.notification_via_email_sms_whatsapp
//                   ? "Yes"
//                   : "No"}
//               </Text>
//               <Text>
//                 <strong>Number of Job Posts:</strong>{" "}
//                 {currentPlan.no_of_job_post}
//               </Text>
//               <Text>
//                 <strong>Number of Urgent Hiring Tags:</strong>{" "}
//                 {currentPlan.no_of_urgent_hiring_tag}
//               </Text>
//               <Text>
//                 <strong>Number of Candidate Unlocks:</strong>{" "}
//                 {currentPlan.no_of_candidate_unlock}
//               </Text>
//               <Text>
//                 <strong>Number of Job Published:</strong>{" "}
//                 {currentPlan.no_of_job_published}
//               </Text>
//               <Text>
//                 <strong>Job Boosts:</strong>{" "}
//                 {currentPlan.job_boosts ? "Yes" : "No"}
//               </Text>
//               <Text>
//                 <strong>Smart Boost via Email/SMS/WhatsApp:</strong>{" "}
//                 {currentPlan.smart_boost_via_email_sms_whatsapp
//                   ? "Yes"
//                   : "No"}
//               </Text>
//               <Text>
//                 <strong>Dedicated Relationship Manager:</strong>{" "}
//                 {currentPlan.dedicated_relationship_manager ? "Yes" : "No"}
//               </Text>
//               <Text>
//                 <strong>Campaign via Email/SMS/WhatsApp:</strong>{" "}
//                 {currentPlan.campion_via_email_sms_whatsapp ? "Yes" : "No"}
//               </Text>
//               <Text>
//                 <strong>Multiple User Login:</strong>{" "}
//                 {currentPlan.multiple_user_login ? "Yes" : "No"}
//               </Text>
//               <Text>
//                 <strong>Reports:</strong> {currentPlan.reports ? "Yes" : "No"}
//               </Text>
//               <Text>
//                 <strong>Company Branding:</strong>{" "}
//                 {currentPlan.company_branding ? "Yes" : "No"}
//               </Text>
//             </VStack>
//           </ModalBody>
//           <ModalFooter>
//             <Button colorScheme="blue" mr={3} onClick={onViewClose}>
//               Close
//             </Button>
//           </ModalFooter>
//         </ModalContent>
//       </Modal>
//     </div>
//   );
// };

// export default SubscriptionCustom;